/**
 * Generative background music, one calm loop per world (WOW · feel). Everything is synthesised with
 * WebAudio from a seeded pattern, so it is ours, costs no download and loops seamlessly:
 *   - each world has a key, tempo, chord loop and its own "instrument" (music box, marimba, harp,
 *     toy piano, dreamy e-piano, celesta) + a soft pad, a light bass and a little world colour
 *     (birds in the meadow, wind in the seasons, ticks in toy town, sparkles in the night city);
 *   - an 8-bar melody is generated once per world from a fixed seed (2-bar motif + variations),
 *     then repeats forever; worlds cross-fade when the child moves between them;
 *   - the music bus follows Settings → Music, the music volume slider, ducks while the teacher
 *     speaks, and pauses in lessons unless live config `musicInLessons` is on.
 */
import type { PathId } from '@sketchling/core';
import { useApp } from '../state/store';
import { audioBuses, setAudioBusyCheck, volumeGain } from './audioBus';
import { isSpeaking } from './tts';
import { ambientAllowed, onPowerChange } from './power';
import { useFeelPrefs } from './feelPrefs';

type Lead = 'musicbox' | 'marimba' | 'harp' | 'toypiano' | 'epiano' | 'celesta';
type Colour = 'birds' | 'wind' | 'tick' | 'sparkle' | 'shaker' | null;

interface WorldStyle {
  /** MIDI note of the tonic. */
  root: number;
  scale: number[];
  bpm: number;
  /** Four chords (semitones from the root), one per bar, played twice in the 8-bar loop. */
  chords: number[][];
  lead: Lead;
  colour: Colour;
  /** Probability of a melody note on an off-beat 8th. */
  density: number;
  seed: number;
  /** Pad brightness (low-pass Hz). */
  padTone: number;
}

const MAJOR_PENTA = [0, 2, 4, 7, 9];
const MINOR_PENTA = [0, 3, 5, 7, 10];
const I = [0, 4, 7];
const vi = [-3, 0, 4];
const IV = [-7, -3, 0];
const V = [-5, -1, 2];
const ii = [2, 5, 9];

export const WORLD_STYLE: Record<PathId, WorldStyle> = {
  intro: { root: 60, scale: MAJOR_PENTA, bpm: 84, chords: [I, vi, IV, V], lead: 'musicbox', colour: null, density: 0.34, seed: 11, padTone: 900 },
  animals: { root: 65, scale: MAJOR_PENTA, bpm: 92, chords: [I, IV, vi, V], lead: 'marimba', colour: 'birds', density: 0.42, seed: 23, padTone: 1000 },
  nature: { root: 62, scale: [0, 2, 4, 6, 7, 9, 11], bpm: 72, chords: [I, ii, IV, I], lead: 'harp', colour: 'wind', density: 0.3, seed: 37, padTone: 800 },
  characters: { root: 67, scale: MAJOR_PENTA, bpm: 104, chords: [I, V, vi, IV], lead: 'toypiano', colour: 'tick', density: 0.46, seed: 41, padTone: 1200 },
  anime: { root: 57, scale: MINOR_PENTA, bpm: 78, chords: [[0, 3, 7], [-4, 0, 3], [3, 7, 10], [-2, 2, 5]], lead: 'epiano', colour: 'sparkle', density: 0.32, seed: 53, padTone: 700 },
  cuteObjects: { root: 70, scale: MAJOR_PENTA, bpm: 96, chords: [I, vi, ii, V], lead: 'celesta', colour: 'shaker', density: 0.4, seed: 67, padTone: 1100 },
};

const hz = (midi: number) => 440 * Math.pow(2, (midi - 69) / 12);

function rng(seed: number) {
  let s = seed >>> 0 || 1;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

interface Note {
  step: number;
  midi: number;
  len: number;
  vel: number;
}

const STEPS_PER_BAR = 8;
const BARS = 8;
export const LOOP_STEPS = STEPS_PER_BAR * BARS;

/** Deterministic 8-bar melody for a world (2-bar motif, answered, varied, resolved). */
export function composeMelody(st: WorldStyle): Note[] {
  const r = rng(st.seed);
  const degrees = st.scale;
  // Scale-degree index → midi in the lead octave.
  const at = (i: number) => st.root + 12 + Math.floor(i / degrees.length) * 12 + degrees[((i % degrees.length) + degrees.length) % degrees.length]!;
  const motif = (start: number, bars: number, from: number): Note[] => {
    const out: Note[] = [];
    let idx = from;
    for (let s = 0; s < bars * STEPS_PER_BAR; s++) {
      const beat = s % 2 === 0;
      const p = s % STEPS_PER_BAR === 0 ? 0.92 : beat ? st.density + 0.25 : st.density * 0.6;
      if (r() > p) continue;
      idx += Math.round((r() - 0.5) * 3.2);
      idx = Math.max(0, Math.min(degrees.length * 2, idx));
      const len = beat && r() < 0.35 ? 2 : 1;
      out.push({ step: start + s, midi: at(idx), len, vel: s % STEPS_PER_BAR === 0 ? 1 : beat ? 0.8 : 0.62 });
    }
    return out;
  };
  const a = motif(0, 2, 2);
  const b = motif(2 * STEPS_PER_BAR, 2, 4);
  // Bars 5-6 repeat the motif a step higher in the scale; bars 7-8 resolve to the tonic.
  const lift = a.map((n) => ({ ...n, step: n.step + 4 * STEPS_PER_BAR, midi: n.midi + (degrees[1] ?? 2) }));
  const end = motif(6 * STEPS_PER_BAR, 1, 3);
  end.push({ step: 7 * STEPS_PER_BAR, midi: st.root + 12, len: 6, vel: 0.9 });
  return [...a, ...b, ...lift, ...end].sort((x, y) => x.step - y.step);
}

/* ------------------------------- instruments ------------------------------- */

function env(ctx: AudioContext, g: GainNode, t: number, peak: number, attack: number, decay: number) {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
}

function partial(ctx: AudioContext, out: AudioNode, f: number, t: number, type: OscillatorType, peak: number, attack: number, decay: number, detune = 0) {
  const o = ctx.createOscillator();
  o.type = type;
  o.frequency.value = f;
  o.detune.value = detune;
  const g = ctx.createGain();
  env(ctx, g, t, peak, attack, decay);
  o.connect(g).connect(out);
  o.start(t);
  o.stop(t + attack + decay + 0.05);
}

function leadNote(ctx: AudioContext, out: AudioNode, lead: Lead, f: number, t: number, vel: number, beat: number) {
  const v = 0.11 * vel;
  switch (lead) {
    case 'musicbox':
      partial(ctx, out, f, t, 'sine', v, 0.004, 1.3);
      partial(ctx, out, f * 2, t, 'sine', v * 0.3, 0.003, 0.5);
      partial(ctx, out, f * 5.4, t, 'sine', v * 0.06, 0.002, 0.15);
      break;
    case 'marimba':
      partial(ctx, out, f, t, 'sine', v * 1.1, 0.004, 0.55);
      partial(ctx, out, f * 4, t, 'sine', v * 0.18, 0.002, 0.09);
      break;
    case 'harp':
      partial(ctx, out, f, t, 'triangle', v * 0.9, 0.006, 1.8);
      partial(ctx, out, f * 2, t, 'sine', v * 0.18, 0.004, 0.9);
      break;
    case 'toypiano':
      partial(ctx, out, f, t, 'triangle', v * 0.9, 0.003, 0.7);
      partial(ctx, out, f * 3.01, t, 'sine', v * 0.16, 0.002, 0.25);
      break;
    case 'epiano':
      partial(ctx, out, f, t, 'sine', v, 0.01, beat * 3);
      partial(ctx, out, f, t, 'sine', v * 0.5, 0.01, beat * 3, 7);
      partial(ctx, out, f * 2, t, 'sine', v * 0.12, 0.008, 0.6);
      break;
    case 'celesta':
      partial(ctx, out, f * 2, t, 'sine', v * 0.8, 0.003, 1.0);
      partial(ctx, out, f * 8, t, 'sine', v * 0.05, 0.002, 0.12);
      break;
  }
}

function padChord(ctx: AudioContext, out: AudioNode, notes: number[], t: number, dur: number, tone: number) {
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = tone;
  lp.Q.value = 0.4;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(0.03, t + Math.min(1.2, dur * 0.4));
  g.gain.setValueAtTime(0.03, t + dur * 0.8);
  g.gain.linearRampToValueAtTime(0.0001, t + dur + 0.6);
  lp.connect(g).connect(out);
  for (const m of notes) {
    for (const d of [-6, 6]) {
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.value = hz(m);
      o.detune.value = d;
      o.connect(lp);
      o.start(t);
      o.stop(t + dur + 0.7);
    }
  }
}

function noiseHit(ctx: AudioContext, out: AudioNode, t: number, dur: number, freq: number, q: number, peak: number) {
  const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const f = ctx.createBiquadFilter();
  f.type = 'bandpass';
  f.frequency.value = freq;
  f.Q.value = q;
  const g = ctx.createGain();
  env(ctx, g, t, peak, Math.min(0.01, dur / 4), dur);
  src.connect(f).connect(g).connect(out);
  src.start(t);
}

function chirp(ctx: AudioContext, out: AudioNode, t: number, r: () => number) {
  const base = 2600 + r() * 1200;
  for (let i = 0; i < 2 + Math.floor(r() * 2); i++) {
    const o = ctx.createOscillator();
    o.type = 'sine';
    const s = t + i * 0.11;
    o.frequency.setValueAtTime(base, s);
    o.frequency.exponentialRampToValueAtTime(base * 1.35, s + 0.06);
    const g = ctx.createGain();
    env(ctx, g, s, 0.012, 0.005, 0.07);
    o.connect(g).connect(out);
    o.start(s);
    o.stop(s + 0.1);
  }
}

/* --------------------------------- engine --------------------------------- */

export interface MusicEngine {
  world: PathId;
  output: GainNode;
  start(): void;
  /** Fade out and stop (seconds). */
  stop(fade?: number): void;
}

/** A looping generative player for one world, writing into `dest` (the music bus or a recorder). */
export function createMusicEngine(ctx: AudioContext, dest: AudioNode, world: PathId, wet?: AudioNode): MusicEngine {
  const st = WORLD_STYLE[world] ?? WORLD_STYLE.intro;
  const melody = composeMelody(st);
  const byStep = new Map<number, Note[]>();
  for (const n of melody) byStep.set(n.step, [...(byStep.get(n.step) ?? []), n]);
  const output = ctx.createGain();
  output.gain.value = 0;
  output.connect(dest);
  const leadBus = ctx.createGain();
  leadBus.gain.value = 1;
  leadBus.connect(output);
  if (wet) {
    const send = ctx.createGain();
    send.gain.value = 0.45;
    leadBus.connect(send).connect(wet);
  }
  const stepDur = 60 / st.bpm / 2;
  const beat = 60 / st.bpm;
  const r = rng(st.seed * 7 + 1);
  let step = 0;
  let next = 0;
  let timer: ReturnType<typeof setInterval> | null = null;

  const schedule = () => {
    while (next < ctx.currentTime + 0.3) {
      const s = step % LOOP_STEPS;
      const bar = Math.floor(s / STEPS_PER_BAR);
      const chord = st.chords[bar % st.chords.length]!;
      if (s % STEPS_PER_BAR === 0) {
        padChord(ctx, output, chord.map((c) => st.root + c), next, STEPS_PER_BAR * stepDur, st.padTone);
      }
      // Bass: chord root on beats 1 and 3.
      if (s % 4 === 0) partial(ctx, output, hz(st.root - 12 + chord[0]! - (chord[0]! > 2 ? 12 : 0)), next, 'sine', 0.05, 0.01, beat * 1.4);
      for (const n of byStep.get(s) ?? []) leadNote(ctx, leadBus, st.lead, hz(n.midi), next, n.vel, beat);
      switch (st.colour) {
        case 'tick':
          if (s % 4 === 2) noiseHit(ctx, output, next, 0.04, 2200, 6, 0.03);
          break;
        case 'shaker':
          noiseHit(ctx, output, next, 0.05, 7000, 1.5, s % 2 ? 0.006 : 0.011);
          break;
        case 'birds':
          if (s % STEPS_PER_BAR === 5 && r() < 0.28) chirp(ctx, output, next, r);
          break;
        case 'sparkle':
          if (s % 2 === 1 && r() < 0.07) partial(ctx, leadBus, hz(st.root + 36 + st.scale[Math.floor(r() * st.scale.length)]!), next, 'sine', 0.018, 0.003, 0.9);
          break;
        case 'wind':
          if (s % (STEPS_PER_BAR * 2) === 0) noiseHit(ctx, output, next, STEPS_PER_BAR * stepDur * 1.6, 500 + r() * 300, 0.7, 0.012);
          break;
      }
      step++;
      next += stepDur;
    }
  };

  return {
    world,
    output,
    start() {
      if (timer) return;
      next = ctx.currentTime + 0.08;
      output.gain.cancelScheduledValues(ctx.currentTime);
      output.gain.setValueAtTime(0.0001, ctx.currentTime);
      output.gain.linearRampToValueAtTime(1, ctx.currentTime + 1.6);
      schedule();
      timer = setInterval(schedule, 60);
    },
    stop(fade = 1.2) {
      const t = ctx.currentTime;
      output.gain.cancelScheduledValues(t);
      output.gain.setValueAtTime(output.gain.value, t);
      output.gain.linearRampToValueAtTime(0.0001, t + fade);
      setTimeout(
        () => {
          if (timer) clearInterval(timer);
          timer = null;
          output.disconnect();
        },
        fade * 1000 + 400,
      );
    },
  };
}

/* ------------------------------ global player ------------------------------ */

let enabled = false;
let world: PathId = 'intro';
let suppressed = false;
let engine: MusicEngine | null = null;
let duckTimer: ReturnType<typeof setInterval> | null = null;
let ducked = false;

// Battery saver / app in the background: no ambient music (lib/power.ts).
const musicOn = () => enabled && !suppressed && ambientAllowed();

function target(): number {
  return volumeGain(useFeelPrefs.getState().musicVolume) * 0.55 * (ducked ? 0.35 : 1);
}

function applyBus(fade = 0.4) {
  const b = audioBuses();
  if (!b) return;
  b.music.gain.cancelScheduledValues(b.ctx.currentTime);
  b.music.gain.setTargetAtTime(musicOn() ? target() : 0, b.ctx.currentTime, fade / 3);
}

function sync() {
  const b = audioBuses();
  if (!b) return;
  const want = musicOn();
  const w = useApp.getState().config.worldMusic === false ? 'intro' : world;
  if (!want) {
    engine?.stop(0.8);
    engine = null;
    applyBus(0.8);
    if (duckTimer) clearInterval(duckTimer);
    duckTimer = null;
    return;
  }
  if (!engine || engine.world !== w) {
    engine?.stop(1.6);
    engine = createMusicEngine(b.ctx, b.music, w, b.room);
    engine.start();
  }
  applyBus(0.6);
  if (!duckTimer) {
    duckTimer = setInterval(() => {
      const speaking = isSpeaking();
      if (speaking !== ducked) {
        ducked = speaking;
        applyBus(0.5);
      }
    }, 250);
  }
}

/** Settings → Music on/off. */
export function setMusicEnabled(on: boolean): void {
  enabled = on;
  sync();
}

/** The world whose music should play (cross-fades when it changes). */
export function setMusicWorld(w: PathId): void {
  if (w === world) return;
  world = w;
  if (enabled) sync();
}

/** Pause the music (lessons without `musicInLessons`, the timelapse preview, ...). */
export function setMusicSuppressed(s: boolean): void {
  if (s === suppressed) return;
  suppressed = s;
  if (enabled) sync();
}

export const currentMusicWorld = () => world;
export const isMusicPlaying = () => !!engine && musicOn();

useFeelPrefs.subscribe((s, p) => s.musicVolume !== p.musicVolume && applyBus(0.2));
// Visibility / battery saver changes start or stop the music; the audio bus may suspend while nothing plays.
onPowerChange(() => enabled && sync());
setAudioBusyCheck(() => !!engine && musicOn());

/* -------------------------------- stings -------------------------------- */

/** A short fanfare in the world's key and instrument (celebrations), on the SFX bus. */
export function worldSting(w: PathId): void {
  if (!useApp.getState().settings.sfx) return;
  const b = audioBuses();
  if (!b) return;
  const st = WORLD_STYLE[w] ?? WORLD_STYLE.intro;
  const t = b.ctx.currentTime + 0.02;
  const send = b.ctx.createGain();
  send.gain.value = 0.4;
  send.connect(b.room);
  const out = b.ctx.createGain();
  out.gain.value = 1.4;
  out.connect(b.sfx);
  out.connect(send);
  const up = [0, 1, 2, 3, 4].map((i) => st.root + 12 + st.scale[i % st.scale.length]! + (i >= st.scale.length ? 12 : 0));
  up.forEach((m, i) => leadNote(b.ctx, out, st.lead, hz(m), t + i * 0.09, 0.9, 0.5));
  for (const c of [0, 4, 7]) partial(b.ctx, out, hz(st.root + 12 + c + (st.scale === MINOR_PENTA && c === 4 ? -1 : 0)), t + 0.5, 'sine', 0.035, 0.02, 1.8);
  leadNote(b.ctx, out, st.lead, hz(st.root + 24), t + 0.5, 1, 0.6);
  setTimeout(() => (out.disconnect(), send.disconnect()), 3500);
}
